import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SnackbarKey, useSnackbar } from 'notistack';
import { H } from 'highlight.run';
import { AlertVariant } from '../../store/ui/types';
import { RootState } from '../../store';
import { AlertSnackbar } from './AlertSnackbar';
import { clearAlertMessage } from '../../store/ui/actions';
import { IgnoreTrackingErrorMessagesSet } from '../../constants/errorConsts/errorCodeSets';
import { useTrackAppVersion } from '../../hooks/useTrackAppVersion';

const Notifier = () => {
  const dispatch = useDispatch();
  const alertMessage = useSelector((state: RootState) => state.ui.alertMessage);
  const alertMessageType = useSelector(
    (state: RootState) => state.ui.alertMessageType,
  );
  const isProgressComplete = useSelector(
    (state: RootState) => state.ui.isProgressComplete,
  );

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  useTrackAppVersion(enqueueSnackbar);

  /**
   * when snackbar is closed, clear the alert message
   * this will avoid snackbar from showing up again on re-render or useEffect change
   */
  const handleCloseRequest = () => {
    dispatch(clearAlertMessage());
  };

  const snackbarContent = React.useCallback(
    (key: SnackbarKey) =>
      alertMessageType ? (
        <div key={key}>
          <AlertSnackbar
            onClose={() => closeSnackbar(key)}
            alertMessage={alertMessage}
            variant={alertMessageType}
          />
        </div>
      ) : null,
    [alertMessage, alertMessageType],
  );

  React.useEffect(() => {
    let timer = 0;
    if (isProgressComplete) {
      timer = window.setTimeout(() => {
        dispatch(clearAlertMessage());
        closeSnackbar('uploadProgress');
      }, 1000);
    }
    return () => clearTimeout(timer);
  }, [isProgressComplete]);

  React.useEffect(() => {
    if (alertMessageType) {
      const enqueueDate = new Date().getTime();
      enqueueSnackbar(alertMessage, {
        persist: alertMessageType === AlertVariant.PROGRESS,
        key:
          alertMessageType === AlertVariant.PROGRESS
            ? 'uploadProgress'
            : `alert-${alertMessageType}-${enqueueDate}`,
        content: snackbarContent,
        onClose: handleCloseRequest,
      });
      // track event for enqueue snackbar
      if (
        alertMessageType === AlertVariant.ERROR &&
        !IgnoreTrackingErrorMessagesSet[alertMessage]
      ) {
        H.track('Error snackbar', {
          message: alertMessage,
        });
      }
    }
  }, [alertMessage, alertMessageType, enqueueSnackbar]);

  return null;
};

export default Notifier;
